import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Tag,
  Package,
  Sticker,
  PencilRuler,
  Boxes,
  Factory,
  Ship,
} from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, MaskedLines, Stagger, StaggerItem, SectionLabel } from "../components/Motion";
import { IMAGES } from "../lib/site";
import { useLightbox, Watermark } from "../components/Lightbox";

const CAPABILITIES = [
  { icon: Tag, title: "Custom Branding", copy: "Your brand applied to retail or bulk packaging by the selected manufacturing partner." },
  { icon: Package, title: "Custom Packaging", copy: "Box sizes, carton configurations and materials matched to your market and channel." },
  { icon: Sticker, title: "Custom Labels", copy: "Label design, languages and regulatory information per your destination market requirements." },
  { icon: PencilRuler, title: "Packaging Development", copy: "Artwork coordination and packaging prototyping with the manufacturing partner before production." },
  { icon: Boxes, title: "MOQ Discussion", copy: "Private-label MOQs depend on product, packaging and partner capability — discussed per project." },
  { icon: Factory, title: "Production Coordination", copy: "We coordinate production runs with the manufacturing partner against your approved specification." },
  { icon: Ship, title: "Export Support", copy: "Documentation, container loading and shipment coordination to your destination port." },
];

export default function PrivateLabel() {
  const openLightbox = useLightbox();
  return (
    <>
      <SEO />
      <div data-testid="private-label-page" className="bg-offwhite">
        <section className="pt-40 pb-20 sm:pb-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="OEM" title="Private Label" />
            <MaskedLines
              lines={["Private Label & OEM", "Coconut Charcoal"]}
              data-testid="private-label-primary-heading"
              className="mt-8 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]"
              delay={0.15}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
                HAQILA coordinates private label coconut charcoal and OEM supply from Indonesia for international
                brands. Custom charcoal packaging and specifications are reviewed with selected manufacturing
                partners, subject to product availability, partner capability and order quantity.
              </p>
            </Reveal>
            <Reveal delay={0.5} className="mt-8">
              <Link
                to="/request-a-quote?request=private-label"
                data-testid="private-label-cta"
                className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                Discuss Private Label
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </section>

        <section className="bg-charcoal text-offwhite py-20 sm:py-28 tech-grid">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
            <SectionLabel index="SCOPE" title="What Can Be Customized" dark />
            <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" gap={0.07}>
              {CAPABILITIES.map((cap, i) => (
                <StaggerItem key={cap.title} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
                  <div
                    data-testid={`oem-${cap.title.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    className="bg-charcoal hover:bg-white/[0.04] p-8 sm:p-10 h-full flex flex-col gap-5 transition-colors duration-300"
                  >
                    <div className="flex items-center justify-between">
                      <cap.icon className="w-6 h-6 text-clay" strokeWidth={1.5} />
                      <span className="font-display text-xs font-semibold tracking-[0.3em] text-offwhite/30">
                        0{i + 1}
                      </span>
                    </div>
                    <h2 className="font-display text-xl font-bold tracking-tight">{cap.title}</h2>
                    <p className="text-sm text-offwhite/55 leading-relaxed">{cap.copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
            <Reveal className="mt-10">
              <p className="text-xs text-offwhite/40 leading-relaxed max-w-2xl">
                Private-label availability is not guaranteed for every product. Each project is reviewed against
                product availability, packaging requirements and order quantity with the relevant manufacturing
                partner.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="py-20 sm:py-28">
          <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
            <Reveal className="lg:col-span-7">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                From artwork approval to a container of your own brand — coordinated through one partner.
              </h2>
              <p className="mt-5 text-base text-charcoal/65 leading-relaxed max-w-xl">
                Share your wholesale private label shisha charcoal or BBQ charcoal requirements. We'll review
                branding, custom specifications and packaging with suitable manufacturing partners before confirming
                feasible options, MOQ and a quotation.
              </p>
              <Link
                to="/request-a-quote?request=private-label"
                data-testid="private-label-bottom-cta"
                className="group mt-8 inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300"
              >
                Discuss Private Label
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div
                className="relative overflow-hidden cursor-zoom-in"
                onClick={() =>
                  openLightbox(
                    [{ src: IMAGES.charcoalClose, alt: "Coconut charcoal briquettes ready for private-label packaging", caption: "Product of Indonesian Manufacturing Partners" }],
                    0
                  )
                }
              >
                <img
                  src={IMAGES.charcoalClose}
                  alt="Coconut charcoal briquettes ready for private-label packaging"
                  loading="lazy"
                  className="w-full h-72 sm:h-96 object-cover img-spotlight"
                />
                <span className="absolute bottom-4 left-4 text-[10px] font-bold uppercase tracking-[0.25em] text-offwhite/80 bg-[#111111]/50 backdrop-blur-sm px-3 py-1.5">
                  Product of Indonesian Manufacturing Partners
                </span>
                <Watermark />
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
}
